import { DEFAULT_UPLOAD_PREVIEW_SIZE } from "~/const";

/* eslint-disable-next-line */
const MIN_AREA = 100 * 100;

export function loadImage(src: string, minTime?: number): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		const startTime = Date.now();

		img.addEventListener("load", () => {
			const diff = Date.now() - startTime;

			if (minTime > 0 && minTime > diff) {
				setTimeout(() => {
					resolve(img);
				}, minTime - diff);
			} else {
				resolve(img);
			}
		});
		img.addEventListener("error", () => reject());
		img.src = src;
	});
}

export function getAspectSize(size: number, imgWidth: number, imgHeight: number) {
	let width = 0;
	let height = 0;

	if (imgWidth > imgHeight) {
		width = imgWidth / imgHeight * size >>> 0;
		height = size;
	} else {
		width = size;
		height = imgHeight / imgWidth * size >>> 0;
	}

	return {
		width,
		height,
	};
}


export function getVideoCover(videoSrc: string, size = DEFAULT_UPLOAD_PREVIEW_SIZE): Promise<string> {
	return new Promise(resolve => {
		const video = document.createElement('video');

		function sendCover(cover: string) {
			resolve(cover);
		}

		function snapImage() {
			const canvas = document.createElement('canvas');
			const aspectSize = getAspectSize(size, video.videoWidth, video.videoHeight);

			canvas.width = aspectSize.width;
			canvas.height = aspectSize.height;
			canvas.getContext('2d')?.drawImage(video, 0, 0, video.videoWidth, video.videoHeight, 0, 0, canvas.width, canvas.height);

			const check = canvas.width * canvas.height > MIN_AREA;

			return {
				image: check
					? canvas.toDataURL()
					: "",
				check,
			};
		}

		function timeupdate() {
			const siDate = snapImage();

			if (siDate.check) {
				video.removeEventListener('timeupdate', timeupdate);
				video.pause();
				video.src = "";
				sendCover(siDate.image);
			}
		}

		video.addEventListener('loadeddata', () => {
			const siDate = snapImage();

			if (siDate.check) {
				video.removeEventListener('timeupdate', timeupdate);
				video.src = "";
				sendCover(siDate.image);
			}
		});
		video.addEventListener('timeupdate', timeupdate);
		video.preload = 'metadata';
		video.src = videoSrc;
		// Load video in Safari / IE11
		video.muted = true;
		video.playsInline = true;
		video.play();
	});
}

export function getFileCover(file: File, size = DEFAULT_UPLOAD_PREVIEW_SIZE): Promise<string> {
	/* eslint-disable no-inner-declarations */
	return new Promise((resolve, reject) => {
		const fileReader = new FileReader();

		if (file.type.match('image')) {
			fileReader.onload = async () => {
				try {
					const img = await loadImage(fileReader.result as string);
					const canvas = document.createElement('canvas');
					const aspectSize = getAspectSize(size, img.naturalWidth, img.naturalHeight);

					canvas.width = aspectSize.width;
					canvas.height = aspectSize.height;
					canvas.getContext('2d')?.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, 0, 0, canvas.width, canvas.height);

					resolve(canvas.toDataURL());
				} catch (exc) {
					reject();
				}
			};
			fileReader.onerror = () => {
				reject();
			};
			fileReader.readAsDataURL(file);
		} else {
			fileReader.onload = () => {
				if (fileReader.result) {
					const blob = new Blob([fileReader.result], { type: file.type });
					const url = URL.createObjectURL(blob);

					getVideoCover(url).then(cover => {
						URL.revokeObjectURL(url);
						resolve(cover);
					});
				} else {
					reject();
				}
			};
			fileReader.onerror = () => {
				reject();
			};
			fileReader.readAsArrayBuffer(file);
		}
	});
}

interface IImageDimension {
	width: number;
	height: number;
}

export function getImageDimensions(file: File): Promise<IImageDimension> {
	return new Promise(resolve => {
		const fileReader = new FileReader();
		const size: IImageDimension = {
			width: 0,
			height: 0,
		};

		fileReader.onload = () => {
			const img = new Image();

			img.addEventListener("load", () => {
				size.width = img.naturalWidth;
				size.height = img.naturalHeight;

				resolve(size);
			});
			img.addEventListener("error", () => {
				resolve(size);
			});
			img.src = fileReader.result as string;
		};
		fileReader.onerror = () => {
			resolve(size);
		};
		fileReader.readAsDataURL(file);
	});
}
